import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import { Alert, Autocomplete } from '@material-ui/lab'
import { useTheme } from '@material-ui/styles'

import { Card, LoadingFeedback } from 'components'
import { ManageCompaniesTable } from './components'

import { useFetch, useSnackbar, usePagination } from 'hooks'

import helpers from 'helpers'
import * as service from 'service'
import schema from './schema'

const ManageCompaniesForm = ({
  ssoCorporateGroup,
  show,
  setRefreshAll,
  refreshAll,
}) => {
  const [companyInputValue, setCompanyInputValue] = useState('')
  const [companies, setCompanies] = useState([])
  const [loadingCompanies, setLoadingCompanies] = useState(false)

  const snackbar = useSnackbar()

  const handleTypeCompaniesInput = (e) => {
    setCompanyInputValue(e.target.value)
  }
  const { handleSubmit, control, reset, errors } = useForm({
    validationSchema: schema,
  })
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'), {
    defaultMatches: true,
  })
  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    usePagination(5)

  const { instructionsText } = helpers.companies.typeAndSearch

  const { response, isLoading, refresh } = useFetch(
    service.dponet.ssoCorporateGroupCompanies.get,
    {
      ssoCorporateGroupId: ssoCorporateGroup?.id,
      perPage,
      page,
      minimal: true,
    },
    [perPage, page, ssoCorporateGroup, refreshAll],
  )

  const handleRefresh = () => {
    setRefreshAll(!refreshAll)
    refresh()
  }

  const onSubmit = async (data) => {
    try {
      setLoadingCompanies(true)
      await service.dponet.ssoCorporateGroupCompanies.create({
        ssoCorporateGroupId: ssoCorporateGroup.id,
        companyId: data.companyId,
      })
      snackbar.open({
        message: 'Empresa vinculada com sucesso!',
        variant: 'success',
      })
      setRefreshAll(!refreshAll)
      reset()
    } catch (error) {
      return snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error || 'Erro ao vincular empresa!',
        ),
        variant: 'error',
      })
    } finally {
      setLoadingCompanies(false)
    }
  }

  const loadCompanies = async () => {
    try {
      setLoadingCompanies(true)
      const companiesResponse = await service.dponet.companies.get({
        responsableUserEmail: ssoCorporateGroup?.domain,
        minimal: true,
        perPage: 1000 * 1000,
        name: companyInputValue,
      })
      setCompanies(companiesResponse?.data?.companies)
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error || 'Erro ao buscar empresas',
        ),
        variant: 'error',
      })
    } finally {
      setLoadingCompanies(false)
    }
  }

  useEffect(() => {
    const getCompanies = setTimeout(() => {
      if (companyInputValue.length < 3) return

      loadCompanies()
    }, 1000)

    return () => clearTimeout(getCompanies)
    // eslint-disable-next-line
  }, [companyInputValue])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <LoadingFeedback open={isLoading} />
      <Card title={'Gerenciamento de Empresas'}>
        {!show && (
          <Grid container spacing={2}>
            <Grid item xs={isMobile ? 12 : 10}>
              <Box mt={1}>
                <Controller
                  control={control}
                  name="companyId"
                  onChange={([, data]) => data?.id}
                  mode="onChange"
                  as={
                    <Autocomplete
                      options={companies || []}
                      noOptionsText={instructionsText(companyInputValue)}
                      getOptionLabel={(option) => option.name || ''}
                      getOptionSelected={(option, value) =>
                        option.id === value.id || value.id === 0
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onChange={handleTypeCompaniesInput}
                          label="Vincular empresa"
                          variant="outlined"
                          error={!!errors.companyId}
                          helperText={errors?.companyId?.message}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingCompanies && (
                                  <CircularProgress color="primary" size={20} />
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={isMobile ? 12 : 2}>
              <Box
                height="100%"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                >
                  Adicionar
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="warning">
                <Typography>
                  Ao vincular uma empresa, todos os usuários com e-mails que
                  compartilham o mesmo domínio do Grupo Empresarial SSO-SAML
                  serão automaticamente associados a esse grupo.
                </Typography>
              </Alert>
            </Grid>
            {/* IMPLEMENTAR FUTURAMENTE
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox checked={allUsers} onChange={handleAllUsers} />
                }
                label="Vincular todas os usários dessa empresa?"
              />
            </Grid> */}
          </Grid>
        )}
        <ManageCompaniesTable
          ssoCorporateGroupCompanies={
            response?.data?.ssoCorporateGroupCompanies
          }
          totalCount={response?.data?.meta?.totalCount}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          page={page}
          perPage={perPage}
          show={show}
          refresh={handleRefresh}
        />
      </Card>
    </form>
  )
}

export default ManageCompaniesForm
